import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { ArrowForwardIcon, Button, Paper, Stack, Typography, Box } from '@esgian/esgianui';
import { DrillingEquipmentChart } from '@components/Charts';
import { DISPLAY_UNIT, SUB_SECTIONS } from '@constants/index';
import { useDisplayUnit, useUpdateSection } from '@hooks';
import { getValue } from '@helpers';
import TextWithTooltipIcon from '@components/Display/TextWithTooltipIcon';
import { findEquipmentSignal } from '@api/helpers';

function DrillingEquipment({ loading, equipmentConfig, equipmentData }) {
  const { updateMainPageSection } = useUpdateSection();
  const { displayUnit } = useDisplayUnit();

  const getSubTitle = useMemo(() => {
    switch (displayUnit) {
      case DISPLAY_UNIT.CO2:
        return 'Total CO2e emissions in period';
      case DISPLAY_UNIT.NOX:
        return 'Total NOx emissions in period';
      case DISPLAY_UNIT.FUEL:
        return 'Total fuel consumption in period';
      case DISPLAY_UNIT.ENERGY:
        return 'Total energy consumption in period';
    }
  }, [displayUnit]);
  return (
    <Paper sx={{ p: 2, height: '100%' }}>
      <Stack spacing={2}>
        <Box>
          <TextWithTooltipIcon
            tooltipText={
              'Total consumption or emissions in the selected time period for each equipment.'
            }
            label={<Typography variant={'h6'}>Equipment</Typography>}
          />
          <Typography variant={'body2'}>{getSubTitle}</Typography>
        </Box>
        {/* compute parsed totals per equipment for current display unit and pass down */}
        {(() => {
          const { displayUnit } = useDisplayUnit();
          const parsedTotals = {};
          if (equipmentData && equipmentConfig?.equipmentDetails) {
            const componentCalculations = equipmentData.componentCalculations || [];
            equipmentConfig.equipmentDetails.forEach(({ equipmentName }) => {
              const signal = findEquipmentSignal(componentCalculations, equipmentName, displayUnit);
              const data = signal?.timeSeriesValues || {};
              if (!data || Object.keys(data).length === 0) return;
              const tot = Object.values(data).reduce((a, b) => a + getValue(b), 0);
              parsedTotals[equipmentName] = tot;
            });
          }

          return (
            <DrillingEquipmentChart
              loading={loading}
              equipmentConfig={equipmentConfig}
              parsedEquipmentTotals={parsedTotals}
            />
          );
        })()}
        <Stack direction={'row'} justifyContent={'flex-end'}>
          <Button
            onClick={() => updateMainPageSection(SUB_SECTIONS.EQUIPMENT)}
            endIcon={<ArrowForwardIcon />}
            variant={'outlined'}>
            See equipment page
          </Button>
        </Stack>
      </Stack>
    </Paper>
  );
}

DrillingEquipment.propTypes = {
  loading: PropTypes.bool,
  overviewData: PropTypes.object
};

DrillingEquipment.defaultProps = {
  loading: false,
  overviewData: null
};

export default DrillingEquipment;
